/**
 * Lane Payload Schemas — the sandbox→browser stream events that had no contract
 * (s2-07d, tracker W2.09): the tool lifecycle side-channel (tool_use:start,
 * tool_progress, status) and the subagent lane (start / text_delta / tool_use /
 * tool_result / end).
 *
 * THE WIRE IS THE TRUTH. Every field here is transcribed from the emitter named
 * on the schema (apps/sandbox + packages/agent-sdk at origin/staging, 2026-09-03);
 * nothing is widened to what the browser happens to read. Where the browser
 * reads a field no emitter sends, that is a finding listed in PR s2-07d-b, not
 * a field here.
 *
 * Relay envelope: forwardToFrontend stamps taskId / userId / agentSlug on every
 * forwarded frame (RelayForwardTags in events/payloads.ts); the schemas
 * describe what the SANDBOX sends, which is what the parse gate sees. The
 * subagent emitter (apps/sandbox/src/harness/tools/subagent-emit.ts) itself
 * prefixes { taskId, toolUseId, agentId, seq } on every subagent:* frame; the
 * engine's up-front subagent:start (round-tool-batch.ts) carries only
 * { taskId, toolUseId, agentSlug }, so agentId / seq are optional.
 */

import { z } from 'zod';

// =============================================================================
// Tool lifecycle side-channel (main agent)
// =============================================================================

/**
 * agent:tool_use:start — apps/sandbox/src/stream/harness-sink.ts:237. Synthesized
 * once per toolUseId before the completed AGENT_TOOL_USE so the card enters its
 * "preparing" phase. sessionId is the task id (the sink's `sessionId: taskId`).
 */
export const AgentToolUseStartPayloadSchema = z.object({
  sessionId: z.string().min(1),
  toolUseId: z.string().min(1),
  toolName: z.string().min(1),
});

/**
 * agent:tool_progress — apps/sandbox/src/harness/tools/monitor-tool.ts:115. One
 * frame per line a monitored background process prints; the ONLY live emitter
 * is the Monitor tool (toolName is always 'Monitor' today, typed as string so a
 * second streaming tool needs no schema change).
 */
export const AgentToolProgressPayloadSchema = z.object({
  taskId: z.string().min(1),
  toolUseId: z.string().min(1),
  toolName: z.string().min(1),
  line: z.string(),
  stream: z.enum(['stdout', 'stderr']),
});

/**
 * agent:status — apps/sandbox/src/stream/run-driver.ts:147 (compactionStatusSink):
 * 'compacting' while a compaction runs, null when it ends. The web lights the
 * "Optimizing context…" indicator from it.
 */
export const AgentStatusPayloadSchema = z.object({
  taskId: z.string().min(1),
  status: z.string().nullable(),
});

/**
 * The agent:status word of a turn waiting on the person to sign in to their plan on the box
 * (engines T2a, harness/engines/cli-common/auth-coordinator.ts). The server keeps it on the task
 * (tasks.engine_wait) so a reload gets the wait back; any other word ends it.
 */
export const ENGINE_SIGN_IN_STATUS = 'engine_connecting';

// =============================================================================
// Subagent lane
// =============================================================================

/** Fields every frame from makeSubagentEmitter carries; the engine's own start omits agentId / seq. */
const SubagentLaneBase = {
  taskId: z.string().min(1),
  toolUseId: z.string().min(1),
  agentId: z.string().min(1).optional(),
  seq: z.number().int().nonnegative().optional(),
};

/**
 * subagent:start — two emitters: packages/agent-sdk/src/engine/round-tool-batch.ts:316
 * (up-front, per concurrent lane: { taskId, toolUseId, agentSlug }) and
 * apps/sandbox/src/harness/tools/agent-tool.ts:895 (the child's real start:
 * + agentName / agentIcon / agentColor / prompt through the emitter's prefix).
 */
export const SubagentStartPayloadSchema = z.object({
  ...SubagentLaneBase,
  agentSlug: z.string().min(1),
  agentName: z.string().optional(),
  agentIcon: z.string().optional(),
  agentColor: z.string().optional(),
  prompt: z.string().optional(),
});

/** subagent:text_delta — agent-tool.ts:1089 `{ delta }`. */
export const SubagentTextDeltaPayloadSchema = z.object({
  ...SubagentLaneBase,
  delta: z.string(),
});

/** subagent:tool_use — agent-tool.ts:1091 `{ toolName, toolInput, subToolUseId }`. */
export const SubagentToolUsePayloadSchema = z.object({
  ...SubagentLaneBase,
  toolName: z.string().min(1),
  toolInput: z.unknown(),
  subToolUseId: z.string().optional(),
});

/** subagent:tool_result — agent-tool.ts:1136 `{ subToolUseId, result }`. */
export const SubagentToolResultPayloadSchema = z.object({
  ...SubagentLaneBase,
  subToolUseId: z.string().optional(),
  result: z.unknown(),
});

/**
 * subagent:end — three shapes, one schema:
 *  - agent-tool.ts:1207 (completed): result, summary, totalToolUseCount,
 *    totalTokens, totalDurationMs, stopReason when a wall closed the lane;
 *  - agent-tool-result.ts wallsEndPayload (refused / stopped / capped / failed):
 *    result, summary, stopReason, totalDurationMs;
 *  - apps/sandbox/src/stream/teammates.ts:122 (relay-flap resync): result +
 *    resync:true, agentId = toolUseId, no seq, no reason.
 */
export const SubagentEndPayloadSchema = z.object({
  ...SubagentLaneBase,
  result: z.string().optional(),
  summary: z.string().optional(),
  stopReason: z.string().optional(),
  totalToolUseCount: z.number().optional(),
  totalTokens: z.number().optional(),
  totalDurationMs: z.number().optional(),
  resync: z.boolean().optional(),
});

export type AgentToolUseStartPayload = z.infer<typeof AgentToolUseStartPayloadSchema>;
export type AgentToolProgressPayload = z.infer<typeof AgentToolProgressPayloadSchema>;
export type AgentStatusPayload = z.infer<typeof AgentStatusPayloadSchema>;
export type SubagentStartPayload = z.infer<typeof SubagentStartPayloadSchema>;
export type SubagentTextDeltaPayload = z.infer<typeof SubagentTextDeltaPayloadSchema>;
export type SubagentToolUsePayload = z.infer<typeof SubagentToolUsePayloadSchema>;
export type SubagentToolResultPayload = z.infer<typeof SubagentToolResultPayloadSchema>;
export type SubagentEndPayload = z.infer<typeof SubagentEndPayloadSchema>;
